// =============================================================================
// FILE: src/App.jsx
// MODULE: Core — Application Router
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   Defines the complete route tree for the application.
//   Uses React Router 6's nested routes to apply guards at the right level.
//
// ROUTE STRUCTURE:
//   /                     → Public landing (redirect based on auth state)
//   /login                → Login page (public)
//   /signup               → Signup page (public)
//   /onboarding           → Protected, requires auth but not onboarded
//   /admin/*              → Protected + admin/super_admin role required
//   /player/*             → Protected + all authenticated roles
//   /match/:id/score      → Protected + admin/teamleader/player (referee via DB check)
//   /superadmin           → Protected + super_admin only
//
// CODE SPLITTING:
//   All page components are lazy-loaded via React.lazy().
//   This splits each route into its own bundle chunk, reducing initial load time.
//   Suspense fallback shows LoadingScreen during chunk download.
//
// SECURITY:
//   ProtectedRoute → checks session + onboarding status
//   RoleGuard      → checks role-based access
//   RLS            → enforces data access at DB level (always on)
// =============================================================================

import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ROUTES, ROLES } from '@/lib/constants'
import { ProtectedRoute, RoleGuard } from '@/components/guards/ProtectedRoute'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// Each import() becomes a separate bundle chunk (code splitting).
// The string comment controls the chunk filename in production builds.
// ---------------------------------------------------------------------------

// Public pages
const LoginPage      = lazy(() => import(/* webpackChunkName: "auth" */ '@/pages/auth/LoginPage'))
const SignupPage     = lazy(() => import(/* webpackChunkName: "auth" */ '@/pages/auth/SignupPage'))
const ForgotPwPage   = lazy(() => import(/* webpackChunkName: "auth" */ '@/pages/auth/ForgotPasswordPage'))
const ResetPwPage    = lazy(() => import(/* webpackChunkName: "auth" */ '@/pages/auth/ResetPasswordPage'))

// Onboarding
const OnboardingPage = lazy(() => import(/* webpackChunkName: "onboarding" */ '@/pages/onboarding/OnboardingPage'))

// Admin pages
const AdminDashboard      = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/admin/DashboardPage'))
const TournamentsPage     = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/tournaments/TournamentsPage'))
const TournamentDetailPage = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/tournaments/TournamentDetailPage'))
const TeamsPage           = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/tournaments/TeamsPage'))
const MatchesPage         = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/tournaments/MatchesPage'))
const AdminSettingsPage   = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/admin/SettingsPage'))
const AdminBillingPage    = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/admin/BillingPage'))
const AdminPlayersPage = lazy(() => import(/* webpackChunkName: "admin" */ '@/pages/admin/PlayersPage'))

// Match scoring (referee view)
const MatchScoringPage    = lazy(() => import(/* webpackChunkName: "scoring" */ '@/pages/matches/MatchScoringPage'))
const VolleyballScoringPage = lazy(() => import(/* webpackChunkName: "scoring" */ '@/pages/matches/VolleyballScoringPage'))
const RugbyScoringPage      = lazy(() => import(/* webpackChunkName: "scoring" */ '@/pages/matches/RugbyScoringPage'))
const AthletesPage    = lazy(() => import(/* webpackChunkName: "athletics" */ '@/pages/athletics/AthletesPage'))
const AthletePage     = lazy(() => import(/* webpackChunkName: "athletics" */ '@/pages/athletics/AthletePage'))
const AthEventsPage   = lazy(() => import(/* webpackChunkName: "athletics" */ '@/pages/athletics/EventsPage'))
const AthEventPage    = lazy(() => import(/* webpackChunkName: "athletics" */ '@/pages/athletics/EventPage'))
const AthJoinPage     = lazy(() => import(/* webpackChunkName: "athletics" */ '@/pages/athletics/JoinEventPage'))

// Player pages
const PlayerDashboard     = lazy(() => import(/* webpackChunkName: "player" */ '@/pages/player/PlayerDashboardPage'))
const PlayerProfilePage   = lazy(() => import(/* webpackChunkName: "player" */ '@/pages/player/PlayerProfilePage'))
const PlayerTournamentPage = lazy(() => import(/* webpackChunkName: "player" */ '@/pages/player/PlayerTournamentPage'))

// Super admin
const SuperAdminPage      = lazy(() => import(/* webpackChunkName: "superadmin" */ '@/pages/admin/SuperAdminPage'))

// 404
const NotFoundPage        = lazy(() => import(/* webpackChunkName: "errors" */ '@/pages/NotFoundPage'))

// ---------------------------------------------------------------------------
// App component — the route tree
// ---------------------------------------------------------------------------
export default function App() {
  return (
    <BrowserRouter>
      {/*
        Suspense catches lazy-loaded chunk downloads.
        Falls back to LoadingScreen while the JS chunk is being fetched.
      */}
      <Suspense fallback={<LoadingScreen message="Loading..." />}>
        <Routes>

          {/* ----------------------------------------------------------------
              PUBLIC ROUTES
              Accessible without authentication.
              Logged-in users navigating to /login or /signup are redirected
              to their dashboard by the page component itself.
          ---------------------------------------------------------------- */}
          <Route path={ROUTES.LOGIN}     element={<LoginPage />} />
          <Route path={ROUTES.SIGNUP}    element={<SignupPage />} />
          <Route path={ROUTES.FORGOT_PW} element={<ForgotPwPage />} />
          <Route path={ROUTES.RESET_PW}  element={<ResetPwPage />} />

          {/* ----------------------------------------------------------------
              ONBOARDING ROUTE
              Requires authentication but not a completed profile.
              ProtectedRoute redirects here if isOnboarded = false.
          ---------------------------------------------------------------- */}
          <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />

          {/* ----------------------------------------------------------------
              ADMIN ROUTES
              Require: authenticated + onboarded + role = admin or super_admin
          ---------------------------------------------------------------- */}
          <Route element={<ProtectedRoute />}>
            <Route element={<RoleGuard roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]} />}>
              <Route path={ROUTES.ADMIN_DASHBOARD}   element={<AdminDashboard />} />
              <Route path={ROUTES.ADMIN_TOURNAMENTS} element={<TournamentsPage />} />
              <Route path={ROUTES.ADMIN_TOURNAMENT}  element={<TournamentDetailPage />} />
              <Route path={ROUTES.ADMIN_TEAMS}       element={<TeamsPage />} />
              <Route path={ROUTES.ADMIN_MATCHES}     element={<MatchesPage />} />
              <Route path={ROUTES.ADMIN_SETTINGS}    element={<AdminSettingsPage />} />
              <Route path={ROUTES.ADMIN_BILLING}     element={<AdminBillingPage />} />
              <Route path={ROUTES.ADMIN_PLAYERS} element={<AdminPlayersPage />} />
              <Route path={ROUTES.ADMIN_ATHLETICS} element={<AthletesPage />} />
              <Route path={ROUTES.ADMIN_ATHLETE}   element={<AthletePage />} />
              <Route path={ROUTES.ADMIN_ATH_EVENTS} element={<AthEventsPage />} />
              <Route path={ROUTES.ADMIN_ATH_EVENT}  element={<AthEventPage />} />
              <Route path={ROUTES.ADMIN_ATH_JOIN}   element={<AthJoinPage />} />
            </Route>
          </Route>

          {/* ----------------------------------------------------------------
              MATCH SCORING ROUTE
              Accessible by admins AND referees (users with an entry in referees table).
              RoleGuard allows all authenticated roles here — actual referee
              permission is enforced by Supabase RLS on match_stats INSERT.
          ---------------------------------------------------------------- */}
          <Route element={<ProtectedRoute />}>
            <Route
              path={ROUTES.MATCH_SCORING}
              element={<MatchScoringPage />}
            />
            {/* Volleyball scoring — separate route, separate table (AD-01 Camino B).
                Requires ROUTES.VOLLEYBALL_SCORING = '/volleyball/:id/score' in constants. */}
            <Route
              path={ROUTES.VOLLEYBALL_SCORING}
              element={<VolleyballScoringPage />}
            />
            {/* Rugby scoring — own table (rugby_matches, migration 007). */}
            <Route
              path={ROUTES.RUGBY_SCORING}
              element={<RugbyScoringPage />}
            />
          </Route>

          {/* ----------------------------------------------------------------
              PLAYER ROUTES
              Accessible by all authenticated roles (players, teamleaders, and admins).
              Admins can preview the player view for testing.
          ---------------------------------------------------------------- */}
          <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.PLAYER_DASHBOARD}  element={<PlayerDashboard />} />
            <Route path={ROUTES.PLAYER_PROFILE}    element={<PlayerProfilePage />} />
            <Route path={ROUTES.PLAYER_TOURNAMENT} element={<PlayerTournamentPage />} />
          </Route>

          {/* ----------------------------------------------------------------
              SUPER ADMIN ROUTES
              Require: role = super_admin only
          ---------------------------------------------------------------- */}
          <Route element={<ProtectedRoute />}>
            <Route element={<RoleGuard roles={[ROLES.SUPER_ADMIN]} />}>
              <Route path={ROUTES.SUPER_ADMIN} element={<SuperAdminPage />} />
            </Route>
          </Route>

          {/* ----------------------------------------------------------------
              ROOT REDIRECT
              '/' redirects based on auth state — handled by LoginPage
          ---------------------------------------------------------------- */}
          <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />

          {/* ----------------------------------------------------------------
              404 CATCH-ALL
              Must be last — catches any unmatched route
          ---------------------------------------------------------------- */}
          <Route path="*" element={<NotFoundPage />} />

        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
